import { Button } from "@/components/button"
import { TooltipProvider } from "@/components/tooltip"
import { BodyXs } from "@/domains/design/components/body-xs"
import { NfpmPendingNotice } from "@/domains/thick-v1/components/nfpm-pending-notice"
import { cn, dollarsPrettified } from "@/domains/library"
import type { ITokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import type { ITokenPercentageSlider } from "@/domains/tokens/components/token-percentage-slider"
import { TokenPercentageSlider } from "@/domains/tokens/components/token-percentage-slider"

export type DecreasePositionViewProps = {
  token0Symbol: string
  token1Symbol: string
  /** Pre-formatted token amounts the user will receive pre-slippage. */
  expectedAmount0Display: string
  expectedAmount1Display: string
  /**
   * USD value of the projected per-token receive. Leave `undefined` when the
   * price feed hasn't resolved so we render a neutral dash instead of "$0.00".
   */
  expectedAmount0Usd: number | undefined
  expectedAmount1Usd: number | undefined
  /**
   * Full underlying token amounts currently sitting in the position. Shown on
   * every "receive" row so users always have context, even at 0%.
   */
  underlyingAmount0Display: string
  underlyingAmount1Display: string
  /** Pre-formatted minimum token amounts (`expected * (1 - slippage)`). */
  minAmount0Display: string
  minAmount1Display: string
  displayRange: string
  isInRange: boolean
  percentToWithdraw: number
  onChangePercent: (percent: number) => void
  withdrawValueUsd: number
  slippagePercent: number
  canSubmit: boolean
  isSubmitting: boolean
  submissionError?: string
  onSubmit: () => void
  /** See `CollectFeesViewProps.nfpmTokenIdPending`. */
  nfpmTokenIdPending?: boolean
}

/**
 * Synthetic "token" that adapts `TokenPercentageSlider` /
 * `TokenPercentageButtons` to the "% of my position" semantic of the decrease
 * flow. The shared components always route their result through
 * `token.calculateValue(fraction)` — here we simply return the percent as a
 * string so the upstream handler can parse it straight back into a number.
 * `symbol: "%"` keeps the hover tooltip readable ("25 %").
 */
const PERCENT_TOKEN: ITokenPercentageSlider & ITokenPercentageButtons = {
  symbol: "%",
  imageURL: "",
  decimals: 0,
  calculateValue: (fraction: number): string => String(Math.round(fraction * 100)),
}

/**
 * Pure "remove liquidity" (decrease) view.
 *
 * Presents a percentage slider paired with 0/25/50/75/100 preset buttons —
 * the same components used by stake / unstake / redeem flows — the projected
 * token outputs, minimum-out amounts with slippage, and a submit button. The
 * range is fixed for an existing position so it's shown read-only so users
 * can confirm they're acting on the right position.
 */
export const DecreasePositionView = ({
  token0Symbol,
  token1Symbol,
  expectedAmount0Display,
  expectedAmount1Display,
  expectedAmount0Usd,
  expectedAmount1Usd,
  underlyingAmount0Display,
  underlyingAmount1Display,
  minAmount0Display,
  minAmount1Display,
  displayRange,
  isInRange,
  percentToWithdraw,
  onChangePercent,
  withdrawValueUsd,
  slippagePercent,
  canSubmit,
  isSubmitting,
  submissionError,
  onSubmit,
  nfpmTokenIdPending = false,
}: DecreasePositionViewProps) => {
  return (
    // `TokenPercentageButtons` renders a Radix `Tooltip` per chip and therefore
    // requires a `TooltipProvider` ancestor. Scope one to this view so the
    // component is self-contained for stories/tests and doesn't depend on
    // whatever layer happens to wrap the composite.
    <TooltipProvider>
    <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-t100">Remove liquidity</h2>
        <span className="text-xs text-t300">Slippage: {slippagePercent}%</span>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-md border border-p300-light bg-p300 px-3 py-2">
        <div className="flex flex-col">
          <BodyXs className="uppercase tracking-wide text-t300">Price range</BodyXs>
          <span className="text-sm font-medium text-t100">{displayRange || "—"}</span>
        </div>
        <span
          className={cn(
            "shrink-0 rounded px-2 py-0.5 text-[10px] font-medium",
            isInRange
              ? "bg-primary-400/20 text-primary-400"
              : "bg-error-background/15 text-error-text",
          )}
        >
          {isInRange ? "In range" : "Out of range"}
        </span>
      </div>

      <div className="flex flex-col gap-4 rounded-lg border border-p300-light bg-p300 p-4">
        <div className="flex items-baseline justify-between">
          <BodyXs className="font-medium uppercase tracking-wide text-t300">
            Withdraw amount
          </BodyXs>
          <span className="text-2xl font-medium tracking-tight text-t100">
            {Math.round(percentToWithdraw)}%
          </span>
        </div>
        <TokenPercentageSlider
          token={PERCENT_TOKEN}
          value={percentToWithdraw}
          onChange={(value) => onChangePercent(Number(value))}
        />
        <TokenPercentageButtons
          token={PERCENT_TOKEN}
          onClick={(value) => onChangePercent(Number(value))}
        />
      </div>

      <div className="flex flex-col gap-2">
        <BodyXs className="font-medium uppercase tracking-wide text-t300">
          You will receive
        </BodyXs>
        <OutputRow
          symbol={token0Symbol}
          amount={expectedAmount0Display}
          amountUsd={expectedAmount0Usd}
          minAmount={minAmount0Display}
          availableAmount={underlyingAmount0Display}
        />
        <OutputRow
          symbol={token1Symbol}
          amount={expectedAmount1Display}
          amountUsd={expectedAmount1Usd}
          minAmount={minAmount1Display}
          availableAmount={underlyingAmount1Display}
        />
      </div>

      <div className="flex items-center justify-between border-t border-p300-light pt-3 text-xs text-t300">
        <span>Total value</span>
        <span className="text-sm font-medium text-t100">
          {dollarsPrettified(withdrawValueUsd)}
        </span>
      </div>

      {nfpmTokenIdPending ? <NfpmPendingNotice actionLabel="Remove liquidity" /> : null}

      {submissionError ? (
        <p className="rounded-md border border-error-border/40 bg-error-background/10 px-3 py-2 text-xs text-error-text">
          {submissionError}
        </p>
      ) : null}

      <Button
        variant={"primary"}
        size={"lg"}
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting}
        className={cn("w-full")}
      >
        {isSubmitting
          ? "Removing liquidity..."
          : nfpmTokenIdPending
            ? "Resolving NFP id…"
            : canSubmit
              ? "Remove liquidity"
              : "Select an amount"}
      </Button>
    </div>
    </TooltipProvider>
  )
}

const OutputRow = ({
  symbol,
  amount,
  amountUsd,
  minAmount,
  availableAmount,
}: {
  symbol: string
  amount: string
  amountUsd: number | undefined
  minAmount: string
  availableAmount: string
}) => (
  <div className="flex items-center justify-between gap-3 rounded-md border border-p300-light bg-p300 px-3 py-2.5">
    <div className="flex min-w-0 flex-col">
      <span className="text-sm font-medium text-t100">{symbol}</span>
      <span className="text-[11px] text-t300">Available: {availableAmount || "0"}</span>
      <span className="text-[11px] text-t300">Min principal: {minAmount || "0"}</span>
    </div>
    <div className="flex flex-col items-end">
      <span className="truncate text-right text-lg font-medium text-t100">
        {amount || "0"}
      </span>
      <span className="text-[11px] text-t300">
        {amountUsd === undefined ? "—" : dollarsPrettified(amountUsd)}
      </span>
    </div>
  </div>
)
